// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import type { PushSettings, PushStatus } from "../lib/api.js";
import { useApp } from "../lib/app.js";
import { useI18n, type I18n } from "../lib/i18n.js";
import type { PushDetail } from "../lib/model.js";
import { allNotices, visibleNotices, type Notice, type NoticeType } from "../lib/notifications.js";
import { useStore } from "../lib/store.js";

const TYPES: readonly NoticeType[] = ["bills", "budgets", "goals"];
const PAGE: Record<NoticeType, string> = { bills: "events", budgets: "budgets", goals: "purchases" };

/** The sentence for one notification. */
export function noticeText(n: Notice, { t, money, date, category }: I18n, today: string): string {
  switch (n.kind) {
    case "bill-due":
      return t("notify.billDue", { title: n.event.title, amount: money(n.event.amount ?? 0), date: date(n.date, "short") });
    case "bill-overdue":
      return t("notify.billOverdue", { title: n.event.title, amount: money(n.event.amount ?? 0), date: date(n.date, "short") });
    case "event-soon":
      return t(n.date === today ? "notify.eventToday" : "notify.eventTomorrow", { title: n.event.title });
    case "budget-over":
      return t("notify.budgetOver", { name: category(n.category), used: money(n.used), budget: money(n.budget) });
    case "budget-near":
      return t("notify.budgetNear", { name: category(n.category), used: money(n.used), budget: money(n.budget) });
    case "goal-ready":
      return t("notify.goalReady", { name: n.purchase.name, amount: money(n.purchase.price) });
    case "goal-behind":
      return n.projected
        ? t("notify.goalBehind", { name: n.purchase.name, target: date(n.purchase.targetDate, "short"), projected: date(n.projected, "short") })
        : t("notify.goalStalled", { name: n.purchase.name, target: date(n.purchase.targetDate, "short") });
  }
}

export function Notifications() {
  const { store, today, announce } = useApp();
  const state = useStore(store);
  const i18n = useI18n();
  const { t } = i18n;
  const all = useMemo(() => allNotices(state, today), [state, today]);
  const shown = visibleNotices(all, state.notify);
  const dismiss = (ids: string[]) =>
    store.dismiss(
      ids,
      all.map((n) => n.id),
    );

  return (
    <>
      <section className="card" aria-labelledby="notices-title">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 id="notices-title">{t("notify.now")}</h2>
          {shown.length > 1 && (
            <button
              type="button"
              onClick={() => {
                dismiss(shown.map((n) => n.id));
                announce(t("notify.dismissedAll"));
              }}
            >
              {t("notify.dismissAll")}
            </button>
          )}
        </div>
        {shown.length === 0 ? (
          <p className="hint">{t("notify.none")}</p>
        ) : (
          TYPES.map((type) => {
            const list = shown.filter((n) => n.type === type);
            if (list.length === 0) return null;
            return (
              <div key={type} className="notice-group">
                <h3>{t(`notify.type.${type}`)}</h3>
                <ul className="notices">
                  {list.map((n) => {
                    const text = noticeText(n, i18n, today);
                    const warn = n.kind === "bill-overdue" || n.kind === "budget-over" || n.kind === "goal-behind";
                    return (
                      <li key={n.id} data-warn={warn || undefined}>
                        <span className="notice-mark" aria-hidden="true">
                          {warn ? "!" : "•"}
                        </span>
                        <span className="notice-text">
                          {warn && <span className="visually-hidden">{t("notify.attention")} </span>}
                          {text}{" "}
                          <a href={`#/${PAGE[n.type]}`}>{t(`notify.open.${n.type}`)}</a>
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            dismiss([n.id]);
                            announce(t("notify.dismissed"));
                          }}
                          aria-label={t("notify.dismissNamed", { text })}
                        >
                          {t("notify.dismiss")}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </section>

      <section className="card" style={{ marginTop: 16 }} aria-labelledby="notify-types-title">
        <h2 id="notify-types-title">{t("notify.show")}</h2>
        <fieldset>
          <legend className="visually-hidden">{t("notify.show")}</legend>
          <div className="stack" style={{ gap: 6 }}>
            {TYPES.map((type) => (
              <label key={type} className="row" style={{ fontWeight: 450 }}>
                <input type="checkbox" checked={state.notify.types[type]} onChange={(e) => store.setNotifyType(type, e.target.checked)} />
                {t(`notify.type.${type}`)}
                <span className="hint">{t(`notify.type.${type}.hint`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <p className="hint">{t("notify.later")}</p>
      </section>

      <PushCard />
    </>
  );
}

const hours = Array.from({ length: 24 }, (_, h) => h);

function PushCard() {
  const { store, announce } = useApp();
  const state = useStore(store);
  const { t, locale } = useI18n();
  const id = useId();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<PushSettings & { detail: PushDetail; quiet: boolean }>(() => ({
    serverUrl: "",
    topic: `almanac-${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`,
    token: undefined,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    remindHour: 8,
    leadDays: 1,
    quietStart: 22,
    quietEnd: 7,
    digest: false,
    detail: state.notify.push?.detail ?? "generic",
    quiet: true,
  }));
  const on = status?.enabled === true;

  useEffect(() => {
    let live = true;
    store.backend.pushStatus().then(
      (s) => {
        if (!live) return;
        setStatus(s);
        if (s.enabled) {
          setForm((f) => ({
            ...f,
            serverUrl: s.serverUrl,
            topic: s.topic,
            timeZone: s.timeZone,
            remindHour: s.remindHour,
            leadDays: s.leadDays,
            digest: s.digest,
            token: undefined,
            quiet: s.quietStart !== null && s.quietEnd !== null,
            quietStart: s.quietStart ?? 22,
            quietEnd: s.quietEnd ?? 7,
          }));
        }
      },
      () => live && setStatus({ enabled: false }),
    );
    return () => {
      live = false;
    };
  }, [store]);

  const hourLabel = (h: number) => new Intl.DateTimeFormat(locale, { hour: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 1, h)));
  const change = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

  const run = async (work: () => Promise<void>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      announce(done);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    const { detail, quiet, ...settings } = form;
    void run(async () => {
      await store.savePush({ ...settings, quietStart: quiet ? form.quietStart : null, quietEnd: quiet ? form.quietEnd : null }, detail);
      setStatus(await store.backend.pushStatus());
      setForm((f) => ({ ...f, token: undefined }));
    }, t(on ? "push.saved" : "push.started"));
  };

  return (
    <section className="card" style={{ marginTop: 16 }} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("push.title")}</h2>
      <p>{t("push.intro")}</p>
      <p className="hint">{t("push.privacy")}</p>
      {status === null ? (
        <p className="hint">{t("common.loading")}</p>
      ) : (
        <form onSubmit={save} className="stack" style={{ gap: 4, marginTop: 12 }}>
          <div className="field">
            <label htmlFor={`${id}-server`}>{t("push.server")}</label>
            <input
              id={`${id}-server`}
              type="url"
              required
              placeholder="https://ntfy.example.ca"
              value={form.serverUrl}
              onChange={(e) => change("serverUrl", e.target.value)}
              aria-describedby={`${id}-server-hint`}
            />
            <p className="hint" id={`${id}-server-hint`}>
              {t("push.serverHint")}
            </p>
          </div>
          <div className="field">
            <label htmlFor={`${id}-topic`}>{t("push.topic")}</label>
            <input
              id={`${id}-topic`}
              type="text"
              required
              pattern="[A-Za-z0-9_\-]{1,64}"
              value={form.topic}
              onChange={(e) => change("topic", e.target.value)}
              aria-describedby={`${id}-topic-hint`}
            />
            <p className="hint" id={`${id}-topic-hint`}>
              {t("push.topicHint")}
            </p>
          </div>
          <div className="field">
            <label htmlFor={`${id}-token`}>{t("push.token")}</label>
            <input
              id={`${id}-token`}
              type="password"
              autoComplete="off"
              value={form.token ?? ""}
              placeholder={on && status.hasToken ? t("push.tokenKept") : ""}
              onChange={(e) => change("token", e.target.value === "" ? undefined : e.target.value)}
              aria-describedby={`${id}-token-hint`}
            />
            <p className="hint" id={`${id}-token-hint`}>
              {t("push.tokenHint")}
            </p>
            {on && status.hasToken && (
              <button type="button" className="link-button" onClick={() => change("token", null)} disabled={form.token === null}>
                {form.token === null ? t("push.tokenRemoving") : t("push.tokenRemove")}
              </button>
            )}
          </div>
          <fieldset>
            <legend>{t("push.detail")}</legend>
            <div className="stack" style={{ gap: 4 }}>
              {(["generic", "titles", "amounts"] as const).map((d) => (
                <label key={d} className="row" style={{ fontWeight: 450 }}>
                  <input type="radio" name={`${id}-detail`} checked={form.detail === d} onChange={() => change("detail", d)} />
                  {t(`push.detail.${d}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
            <div className="field">
              <label htmlFor={`${id}-hour`}>{t("push.remindHour")}</label>
              <select id={`${id}-hour`} value={form.remindHour} onChange={(e) => change("remindHour", Number(e.target.value))}>
                {hours.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor={`${id}-lead`}>{t("push.leadDays")}</label>
              <select id={`${id}-lead`} value={form.leadDays} onChange={(e) => change("leadDays", Number(e.target.value))}>
                {[0, 1, 2, 3, 7].map((d) => (
                  <option key={d} value={d}>
                    {d === 0 ? t("push.lead.same") : t("push.lead.days", { count: d })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="row" style={{ fontWeight: 450 }}>
            <input type="checkbox" checked={form.quiet} onChange={(e) => change("quiet", e.target.checked)} />
            {t("push.quiet")}
          </label>
          {form.quiet && (
            <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
              <div className="field">
                <label htmlFor={`${id}-qs`}>{t("push.quietStart")}</label>
                <select id={`${id}-qs`} value={form.quietStart ?? 22} onChange={(e) => change("quietStart", Number(e.target.value))}>
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor={`${id}-qe`}>{t("push.quietEnd")}</label>
                <select id={`${id}-qe`} value={form.quietEnd ?? 7} onChange={(e) => change("quietEnd", Number(e.target.value))}>
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <label className="row" style={{ fontWeight: 450 }}>
            <input type="checkbox" checked={form.digest} onChange={(e) => change("digest", e.target.checked)} />
            {t("push.digest")}
          </label>
          <p className="hint">{t("push.timeZone", { zone: form.timeZone })}</p>
          {(error || (on && status.lastError)) && (
            <p className="negative" role="alert">
              {t("push.error", { message: error ?? (on ? status.lastError : "") ?? "" })}
            </p>
          )}
          {store.backend.demo && <p className="hint">{t("push.demo")}</p>}
          <div className="row" style={{ marginTop: 8, flexWrap: "wrap" }}>
            <button type="submit" className="primary" disabled={busy}>
              {on ? t("push.save") : t("push.start")}
            </button>
            {on && (
              <>
                <button type="button" disabled={busy} onClick={() => void run(() => store.backend.testPush(), t("push.testSent"))}>
                  {t("push.sendTest")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await store.stopPush();
                      setStatus({ enabled: false });
                    }, t("push.stopped"))
                  }
                >
                  {t("push.stop")}
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
